import { useCallback, useId, useMemo, useState, type SubmitEvent } from "react";
import { useTranslation } from "react-i18next";
import { suggestGroupMembers } from "../../../api/access/accessApi";
import type { AccountSuggestion } from "../../../api/access/accessModel";
import { invite, type IssuedInvitation } from "../../../api/invitationsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { InvitationLink, PersonSuggestField } from "../../../features/access";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import Field from "../../../ui/forms/Field";
import { FORM_CARD_TIGHT_CLASS, FORM_HELP_TEXT_CLASS, FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";

interface AddMemberFieldProps {
  /** The groups the viewer may put people into here, with their names, first on top. */
  groups: readonly { readonly groupId: string; readonly name: string }[];
  /** Preselected among `groups`. */
  defaultGroupId: string;
  /** Puts the chosen account into the group; the change applies at once, with Undo. */
  onAddToGroup: (groupId: string, person: AccountSuggestion) => void;
}

/**
 * Adds someone to the project in one field (F15), as GitHub, GitLab, Linear and Notion do.
 * Typing a name, or a whole email, suggests accounts not in the chosen group yet; choosing one
 * puts it into that group. Only when the lookup finds no account does the field invite the
 * typed email into that group, with no approval (R3-4, GS-7), and the acceptance link shows
 * once for the inviter to send on (DMW sends no mail yet).
 */
export default function AddMemberField({ groups, defaultGroupId, onAddToGroup }: AddMemberFieldProps) {
  const { t } = useTranslation();
  const fieldId = useId();
  const [chosen, setChosen] = useState<AccountSuggestion | null>(null);
  const [typed, setTyped] = useState({ text: "", nothingMatches: false });
  const [resets, setResets] = useState(0);
  const [groupId, setGroupId] = useState(defaultGroupId);
  const [issued, setIssued] = useState<IssuedInvitation | null>(null);
  const { state, submit } = useSubmission();
  const target = groups.some((group) => group.groupId === groupId) ? groupId : (groups[0]?.groupId ?? "");
  // The chosen group's managers' suggestions: accounts not in it yet (F15).
  const suggest = useMemo(() => (query: string, signal: AbortSignal) => suggestGroupMembers(target, query, signal), [target]);
  const onTyped = useCallback((text: string, nothingMatches: boolean) => {
    setTyped((previous) => (previous.text === text && previous.nothingMatches === nothingMatches ? previous : { text, nothingMatches }));
    setChosen((previous) => (previous !== null && previous.displayName !== text ? null : previous));
  }, []);
  const mayInvite = chosen === null && typed.nothingMatches && target !== "";

  const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (chosen !== null && target !== "") {
      onAddToGroup(target, chosen);
      setChosen(null);
      setResets(resets + 1);
      return;
    }
    if (!mayInvite) return;
    submit(async () => {
      setIssued(await invite({ email: typed.text, accountRole: "member", groups: [target] }));
      setChosen(null);
      setResets(resets + 1);
    });
  };

  return (
    <form onSubmit={handleSubmit} className={FORM_CARD_TIGHT_CLASS}>
      <div className="flex flex-wrap items-end gap-2">
        <Field id={`${fieldId}-group`} label={t("access.group")}>
          {(control) => (
            <select {...control} className={FORM_SELECT_CLASS} value={target} onChange={(event) => { setGroupId(event.target.value); }}>
              {groups.map((group) => <option key={group.groupId} value={group.groupId}>{group.name}</option>)}
            </select>
          )}
        </Field>
        <PersonSuggestField key={resets} label={t("projects.members.add.field")} suggest={suggest} describedBy={`${fieldId}-help`}
          onChoose={setChosen} onTyped={onTyped} />
        <AppButton type="submit" variant={typed.nothingMatches ? "secondary" : "primary"} size="sm"
          disabled={state.status === "submitting" || (chosen === null ? !mayInvite : target === "")}>
          {state.status === "submitting" ? t("login.pleaseWait") : t(typed.nothingMatches && chosen === null ? "projects.members.add.invite" : "projects.members.add.submit")}
        </AppButton>
      </div>
      <p id={`${fieldId}-help`} className={FORM_HELP_TEXT_CLASS} aria-live="polite">
        {chosen === null ? t("projects.members.add.help") : t("projects.members.add.chosen", { name: chosen.displayName, emailHint: chosen.emailHint })}
      </p>
      {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
      {issued !== null ? <InvitationLink issued={issued} /> : null}
    </form>
  );
}
